/**
 * Tracking Core - Simple transport management
 *
 * Replaces TrackingService class with module-level functions.
 */

import { isProduction } from '@client/components/auth';
import {
  Transport,
  TransportConfig,
  TrackingPayload,
  IdentityPayload,
} from './tracking.types';
import {
  createMixpanelTransport,
  createCustomerIOTransport,
  createConsoleTransport,
} from './transports';

// Module state
const transports: Transport[] = [];
let initialized = false;

// Default tokens
const MIXPANEL_DEV_TOKEN = 'c4d78278547dfda40ab55e96d08f206f';
const MIXPANEL_PROD_TOKEN = 'd0cc7240d1640ac4b0cc271807bced6b';

/**
 * Register and initialize a transport.
 */
export const registerTransport = (
  transport: Transport,
  config: TransportConfig,
): void => {
  // Check if already registered
  const existing = transports.find((t) => t.name === transport.name);
  if (existing) {
    console.warn(`Transport "${transport.name}" already registered, skipping`);
    return;
  }

  transport.init(config);
  transports.push(transport);
  console.info(`Tracking: registered "${transport.name}"`);
};

/**
 * Initialize tracking with default transports (Mixpanel + Customer.io).
 */
export const init = (): void => {
  if (initialized) return;

  // Mixpanel
  const mixpanelConfig = {
    type: 'mixpanel' as const,
    token: isProduction() ? MIXPANEL_PROD_TOKEN : MIXPANEL_DEV_TOKEN,
    debug: !isProduction(),
  };
  registerTransport(createMixpanelTransport(), mixpanelConfig);

  // Customer.io (if configured)
  const customerIOSiteId = import.meta.env.VITE_CUSTOMERIO_SITE_ID;
  if (customerIOSiteId) {
    const customerIOConfig = {
      type: 'customerio' as const,
      siteId: customerIOSiteId,
    };
    registerTransport(createCustomerIOTransport(), customerIOConfig);
  }

  initialized = true;
};

/**
 * Track an event across all registered transports.
 */
export const track = (eventName: string, payload: TrackingPayload): void => {
  transports.forEach((transport) => {
    try {
      transport.track(eventName, payload);
    } catch (error) {
      console.error(`Tracking: ${transport.name} track error:`, error);
    }
  });
};

/**
 * Identify user across all transports that support it.
 */
export const identify = (payload: IdentityPayload): void => {
  transports.forEach((transport) => {
    try {
      transport.identify?.(payload);
    } catch (error) {
      console.error(`Tracking: ${transport.name} identify error:`, error);
    }
  });
};

/**
 * Get all registered transports (for testing/debugging).
 */
export const getTransports = (): readonly Transport[] => transports;

/**
 * Reset module state (for testing).
 */
export const reset = (): void => {
  transports.length = 0;
  initialized = false;
};

/**
 * Check if initialized.
 */
export const isInitialized = (): boolean => initialized;

/**
 * Enable console transport for debugging.
 */
export const enableConsoleTransport = (options?: { prefix?: string }): void => {
  const config = {
    type: 'console' as const,
    enabled: true,
    prefix: options?.prefix,
  };
  registerTransport(createConsoleTransport(config), config);
};
